import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  IAuthCompleteRegistrationRequest,
  IAuthRegistrationPayload,
  IResetPasswordResetRequest,
} from '@src/api/types/auth.types';
import { QUERY_KEYS, STORAGE_KEY } from '@src/constants';
import { showSorryNoWorriesAlert } from '@src/helpers/alert.helpers';
import { authLoginUtils } from '@src/helpers/auth.helpers';
import {
  isEmailAlreadyInUse,
  isTokenInvalidError,
  isUserAlreadyExist,
  isUserNotFoundError,
} from '@src/helpers/axios.helpers';
import { getActualFastingStartTime } from '@src/helpers/fasting.helpers';
import { getGenderType } from '@src/helpers/gender.helpers';
import { getCmFromHeightObj } from '@src/helpers/height.helpers';
import { getKgFromWeightObj } from '@src/helpers/weight.helpers';
import {
  ILoginForm,
  IResetPasswordForm,
} from '@src/interfaces/forms.interfaces';
import { IStateContextData } from '@src/interfaces/state.interfaces';
import { IDefaultError } from '@src/interfaces/tanstack.interfaces';

import { authService } from '../services';

interface IUseMutationAuthLoginOptions {
  onSuccess?: () => void;
}

export const useMutationAuthLogin = ({
  onSuccess,
}: IUseMutationAuthLoginOptions = {}) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ILoginForm) => {
      const body = {
        ...data,
        locale: i18n.language,
      };

      const response = await authService.login(body);
      return response.data;
    },
    onSuccess: async (newData) => {
      await authLoginUtils(newData);

      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.subscriptionsMe] });
      onSuccess?.();
    },
  });
};

interface IUseMutationAuthForgotPasswordOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

export const useMutationAuthForgotPassword = ({
  onSuccess,
  onError,
}: IUseMutationAuthForgotPasswordOptions = {}) => {
  return useMutation({
    mutationFn: async (data: IResetPasswordForm) =>
      authService.sendPasswordResetLink(data),
    onSuccess: (data) => {
      if (isUserNotFoundError(data)) {
        onError?.();
        return;
      }
      onSuccess?.();
    },
    onError: (e) => {
      if (isUserNotFoundError(e)) onError?.();
    },
  });
};

interface IUseMutationAuthRegistration {
  onSuccess?: () => void;
  /** Return true when the error was handled by the quiz UI. */
  onError?: (e: IDefaultError) => boolean | void;
}

export const useMutationAuthRegistration = ({
  onSuccess,
  onError,
}: IUseMutationAuthRegistration) => {
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: async (data: IStateContextData) => {
      const body: IAuthRegistrationPayload = {
        email: data.email,
        locale: i18n.language,
        name: data.name,
        profile: {
          activity_level: data.physically_active,
          gender: getGenderType(data.gender),
          height: getCmFromHeightObj(data.height),
          age: data.yourAge,
          weight: {
            start: getKgFromWeightObj(data.currentWeight),
            target: getKgFromWeightObj(data.targetWeight),
          },
        },
        questionnaire: {
          working_hours: data.working_hours,
          sleep_time: data.sleep,
          fasting_on_weekends_type: data.fasting_weekends,
          dinner_time: data.time_dinner,
          lunch_time: data.time_lunch,
          breakfast_time: data.time_breakfast,
          most_hungry_time: data.feel_hungry,
          familiar_level: data.intermittentFasting,
        },
      };

      if (data.name !== undefined) {
        sessionStorage.removeItem(STORAGE_KEY.PROMO_CODE);
      }

      const fastingStart = getActualFastingStartTime(data);
      if (fastingStart && body.profile)
        body.profile.fasting_start = fastingStart;

      const response = await authService.registration(body);
      if (!response.data) throw response;
      return response.data;
    },
    onMutate: () => {
      sessionStorage.removeItem(STORAGE_KEY.EXIT_DISCOUNT_MODAL);
      sessionStorage.removeItem(STORAGE_KEY.APPLY_DISCOUNT);
    },
    onSuccess: async (data) => {
      await authLoginUtils(data);
      onSuccess?.();
    },
    onError: (e) => {
      if (onError?.(e)) return;

      const isEmailOrUserExist =
        isEmailAlreadyInUse(e) || isUserAlreadyExist(e);

      if (!isEmailOrUserExist) {
        showSorryNoWorriesAlert(e.message);
      }
    },
  });
};

interface IUseAuthCompleteRegistrationOptions {
  onSuccess?: () => void;
  payment_intent?: string | null;
}

export const useAuthCompleteRegistration = ({
  onSuccess,
  payment_intent,
}: IUseAuthCompleteRegistrationOptions = {}) => {
  return useMutation({
    mutationFn: async (data: IAuthCompleteRegistrationRequest) => {
      if (!payment_intent) return;

      const response = await authService.completeRegistration({
        ...data,
        transaction: payment_intent,
      });

      return response.data;
    },
    onMutate: () => {
      sessionStorage.removeItem(STORAGE_KEY.EXIT_DISCOUNT_MODAL);
      sessionStorage.removeItem(STORAGE_KEY.APPLY_DISCOUNT);
    },
    onSuccess: (data) => {
      authLoginUtils(data);
      onSuccess?.();
    },
  });
};

interface IUseMutationAuthResetPasswordReset {
  onSuccess?: () => void;
  onTokenError?: () => void;
}

export const useMutationAuthResetPasswordReset = ({
  onSuccess,
  onTokenError,
}: IUseMutationAuthResetPasswordReset = {}) => {
  return useMutation({
    mutationFn: async (data: IResetPasswordResetRequest) =>
      authService.resetPasswordReset(data),
    onSuccess: (data) => {
      if (isTokenInvalidError(data)) {
        onTokenError?.();
        return;
      }
      onSuccess?.();
    },
  });
};
